/**
 * AUTOMATION LIST (TS-52 / TS-65) — Tab "Elenco automazioni" della pagina
 * Automazioni. Intestazione fuori dalla card (titolo, conteggio, ricerca e
 * "Nuova automazione"), poi la card con i filtri di stato a pillola e la griglia
 * con nome/sottotitolo, stato, statistiche esecuzioni e menu azioni.
 *
 * <p>La griglia è una `DataGrid`: ordinamento, menu di colonna e paginazione
 * sono funzioni sue, e una tabella scritta a mano le avrebbe perse. Le CELLE
 * però sono marcatura del design system (`a-name`, `chip`, `a-num`, `lc-when`) e
 * non componenti MUI, e la veste è `tk-grid` come nelle altre liste ristilate:
 * la griglia decide il comportamento, il foglio di stile decide l'aspetto.
 * Con questo sono cadute anche le sei annotazioni di code review sugli stili
 * inline che stavano qui in testa — non c'è più nessuno `sx`/`style` in pagina.
 *
 * Allineato al backend (AutomationsResourceImpl): l'elenco arriva da
 * GET /automations/{groupId} (DTO AutomationDefinition, con `automationCategoryName`
 * denormalizzato); le statistiche (Esecuzioni/Successi/Errori/Ultima) da un
 * endpoint separato GET /automation/{id}/stats, caricato per gli id a video in
 * `statsById`. Le azioni del menu di riga dispatchano le transizioni reali
 * (suspend/resume) e il delete, e portano a dettaglio e monitoraggio. Con MSW
 * attivo la lista si popola dai mock.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { formatRelativeDate } from '@crm/shared';
import { SearchGlyph, PlusGlyph } from '../../components/icons';
import { useToast } from '../../components/useToast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getEntityLabelByFqn } from '../../entityRegistry/entityRegistry';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import {
  deleteAutomation,
  fetchAutomationStats,
  fetchAutomations,
  resumeAutomation,
  suspendAutomation,
  FETCH_ALL_PAGE_SIZE,
} from './automationThunks';
import type { Automation, AutomationStats, AutomationStatus } from './automationTypes';

type StatusFilter = 'all' | AutomationStatus;

// L'elenco esclude gli archiviati (stato terminale): nessun tab dedicato.
const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'draft', 'suspended'];

/** Righe per pagina: la lista arriva tutta dal server e si pagina qui. */
const PAGE_SIZE = 10;

/**
 * Stato -> variante del chip del design system. `STATUS_CHIP_COLOR` in
 * automationTypes parla la lingua di MUI ('success', 'warning'); qui servono i
 * nomi dei semantici del design system.
 */
const STATUS_CHIP_TONE: Record<AutomationStatus, string> = {
  active: 'ok',
  draft: 'neutral',
  suspended: 'warn',
  archived: 'neutral',
};

/** Ultimo segmento del FQN (es. Person) — fallback grezzo. */
/** Data ISO -> istante esatto in ora locale, formato italiano 24h "DD/MM/YYYY HH:mm:ss". */
function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

export default function AutomationList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirmDialog();
  // Locale attivo per le formattazioni numeriche/relative (Intl vuole BCP-47
  // con trattino: it_IT -> it-IT).
  const locale = i18n.language.replace(/_/g, '-');
  const { items: allItems, status, statsById } = useAppSelector((state) => state.automations);
  // Le automazioni di un tipo spento dalla pagina «Voci del CRM» (FILO-53) non si elencano,
  // nemmeno nei conteggi delle pillole.
  const isClassNameDisabled = useIsClassNameDisabled();
  const items = useMemo(
    () => allItems.filter((a) => !isClassNameDisabled(a.className)),
    [allItems, isClassNameDisabled],
  );
  // Le azioni del menu di riga (modifica/transizioni/elimina) sono riservate
  // all'amministratore CRM; l'enforce reale resta lato server (permission check).
  const isAdmin = useAppSelector((s) => s.auth.user?.administrator) ?? false;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchAutomations({ template: false, pageSize: FETCH_ALL_PAGE_SIZE }));
  }, [dispatch]);

  // Statistiche per riga: endpoint per-automazione, caricato per gli id a video.
  // Finche' il motore non scrive run, i contatori tornano 0.
  useEffect(() => {
    for (const a of items)
      dispatch(fetchAutomationStats({ automationDefinitionId: a.automationDefinitionId }));
  }, [items, dispatch]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (
        needle &&
        !a.name.toLowerCase().includes(needle) &&
        !(a.automationCategoryName?.toLowerCase().includes(needle) ?? false)
      ) {
        return false;
      }
      return true;
    });
  }, [items, statusFilter, search]);

  // Conteggio per pillola di filtro: sul set completo, non sul filtrato — deve
  // dire quante ce ne sono per ogni stato, non quante ne resterebbero.
  const countByFilter = useMemo(
    () => Object.fromEntries(
      STATUS_FILTERS.map((f) => [f, f === 'all' ? items.length : items.filter((a) => a.status === f).length]),
    ) as Record<StatusFilter, number>,
    [items],
  );

  const runTransition = async (
    thunk: typeof suspendAutomation | typeof resumeAutomation,
    id: number,
    successKey: string,
    reload = false,
  ) => {
    try {
      await dispatch(thunk(id)).unwrap();
      toast({ severity: 'success', message: t(successKey) });
      // Riattivare un'automazione archivia lato server le versioni inferiori della
      // famiglia: ricarico l'elenco così le archiviate spariscono (l'update
      // ottimistico dello slice aggiorna solo l'automazione riattivata).
      if (reload) dispatch(fetchAutomations({ template: false, pageSize: FETCH_ALL_PAGE_SIZE }));
    } catch (err) {
      toast({ severity: 'error', message: String(err) });
    }
  };

  const performDelete = async (id: number) => {
    try {
      await dispatch(deleteAutomation(id)).unwrap();
      toast({ severity: 'success', message: t('automationsInsights.list.toast.deleted') });
    } catch (err) {
      toast({ severity: 'error', message: String(err) });
    }
  };

  const handlers: RowHandlers = {
    onDetail: (id) => navigate(`/automations/${id}`),
    onMonitor: (id) => navigate(`/automations/${id}/monitoring`),
    onSuspend: (id) =>
      confirm.requestConfirm(
        t('automationsInsights.list.confirmSuspend'),
        () => runTransition(suspendAutomation, id, 'automationsInsights.list.toast.suspended'),
        { confirmColor: 'primary', confirmLabel: t('automationsInsights.list.actions.suspend') }),
    onResume: (id) =>
      confirm.requestConfirm(
        t('automationsInsights.list.confirmResume'),
        () => runTransition(resumeAutomation, id, 'automationsInsights.list.toast.resumed', true),
        { confirmColor: 'primary', confirmLabel: t('automationsInsights.list.actions.resume') }),
    // Unica azione di riga con la parola da ridigitare: sospendere e riattivare si disfano
    // premendo la voce gemella, eliminare no. Qui serve più che altrove — dal menu di riga si
    // elimina un record che non si è aperto, riconosciuto per la sola riga su cui si è cliccato.
    onDelete: (id) =>
      confirm.requestConfirm(t('automationsInsights.list.confirmDelete'), () => performDelete(id), {
        requireTypedConfirmation: true,
      }),
  };

  return (
    <div>
      {/* Intestazione fuori dalla card, come nel concept liste. */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('automationsInsights.title')}</h1>
          <span className="page-sub">{t('common.record', { count: filtered.length })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('automationsInsights.list.searchPlaceholder')}
              aria-label={t('automationsInsights.list.searchPlaceholder')}
            />
          </div>
          <button type="button" className="btn-primary" onClick={() => navigate('/automations/templates')}>
            <PlusGlyph />
            {t('automationsInsights.list.newAutomation')}
          </button>
        </div>
      </div>

      <div className="list-card atable">
        {/* Filtri come pillole, non Tabs MUI: stesso linguaggio delle altre
            liste del restyle. */}
        <div className="list-tabs">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={statusFilter === f ? 'on' : undefined}
              aria-pressed={statusFilter === f}
              onClick={() => setStatusFilter(f)}
            >
              {t(`automationsInsights.list.filters.${f}`)}
              <span className="cnt">{countByFilter[f]}</span>
            </button>
          ))}
        </div>

        {/* La griglia resta: ordinamento, menu di colonna e paginazione sono
            funzioni sue e riscriverle a mano le avrebbe perse. Cambia la veste
            (`tk-grid`) e la marcatura delle celle, non il componente. */}
        <DataGrid
          rows={filtered}
          columns={automationColumns(t, statsById, handlers, locale, isAdmin)}
          getRowId={(row) => row.automationDefinitionId}
          autoHeight
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: PAGE_SIZE, page: 0 } } }}
          loading={status === 'loading'}
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? '' : 'even-row')}
          className="tk-grid"
          slots={{
            noRowsOverlay: () => (
              <div className="ltable__empty">{t('automationsInsights.list.empty')}</div>
            ),
          }}
        />
      </div>
      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
    </div>
  );
}

/** Cella metrica: '—' in grigio quando il valore non c'è (vedi metricValue). */
function MetricCell({ value, locale, tone }: { value: number | undefined; locale: string; tone?: 'ok' | 'err' }) {
  if (value === undefined) return <span className="a-num muted">—</span>;
  return <span className={`a-num${tone ? ` ${tone}` : ''}`}>{value.toLocaleString(locale)}</span>;
}


/**
 * Valore da mostrare in una colonna metrica: `undefined` (→ '—') se le stats non
 * sono ancora caricate o se `runCount` è 0 (automazione mai eseguita: successi ed
 * errori sono per forza 0, quindi trattino su tutte e tre invece di "0").
 */
function metricValue(stats: AutomationStats | undefined, value: number | undefined): number | undefined {
  if (!stats || stats.runCount === 0) return undefined;
  return value;
}

/**
 * Colonne della griglia. Le celle sono disegnate con la marcatura del design
 * system (`a-name`, `chip`, `a-num`, `lc-when`) e non con i componenti MUI:
 * la griglia serve per quello che sa fare — ordinare, paginare, il menu di
 * colonna — non per come rende una cella, che è il mestiere del foglio di stile.
 */
function automationColumns(
  t: (key: string, opts?: Record<string, unknown>) => string,
  statsById: Record<number, AutomationStats>,
  handlers: RowHandlers,
  locale: string,
  isAdmin: boolean,
): GridColDef<Automation>[] {
  return [
    {
      field: 'name', headerName: t('automationsInsights.list.columns.name'), flex: 2, minWidth: 240,
      renderCell: (params) => {
        const subtitle = [getEntityLabelByFqn(params.row.className, t), params.row.automationCategoryName]
          .filter(Boolean)
          .join(' · ');
        return (
          <span className="a-name">
            <span className="a-name__top">
              {/* Il nome apre la vista read-only del builder, come in ogni altro elenco, ed è
                  da lì che si compone: modifica, nuova versione e pubblicazione non stanno
                  più nel menu di riga. */}
              <Link to={`/automations/${params.row.automationDefinitionId}`} className="cell-link">
                {params.row.name}
              </Link>
              <span className="a-ver">v{params.row.version}</span>
            </span>
            <span className="a-name__sub">{subtitle}</span>
          </span>
        );
      },
    },
    {
      field: 'status', headerName: t('automationsInsights.list.columns.status'), width: 130,
      renderCell: (params) => (
        <span className={`chip ${STATUS_CHIP_TONE[params.row.status]}`}>
          {t(`automationsInsights.list.statuses.${params.row.status}`)}
        </span>
      ),
    },
    {
      field: 'executions', headerName: t('automationsInsights.list.columns.executions'), width: 120, sortable: false,
      renderCell: (params) => {
        const stats = statsById[params.row.automationDefinitionId];
        return <MetricCell value={metricValue(stats, stats?.runCount)} locale={locale} />;
      },
    },
    {
      field: 'successes', headerName: t('automationsInsights.list.columns.successes'), width: 110, sortable: false,
      renderCell: (params) => {
        const stats = statsById[params.row.automationDefinitionId];
        const successes = metricValue(stats, stats?.successCount);
        return <MetricCell value={successes} locale={locale} tone={successes ? 'ok' : undefined} />;
      },
    },
    {
      field: 'errors', headerName: t('automationsInsights.list.columns.errors'), width: 100, sortable: false,
      renderCell: (params) => {
        const stats = statsById[params.row.automationDefinitionId];
        const failures = metricValue(stats, stats?.failureCount);
        return <MetricCell value={failures} locale={locale} tone={failures ? 'err' : undefined} />;
      },
    },
    {
      field: 'lastRun', headerName: t('automationsInsights.list.columns.lastRun'), width: 130, sortable: false,
      renderCell: (params) => {
        const lastRunDate = statsById[params.row.automationDefinitionId]?.lastRunDate ?? null;
        // L'istante esatto sta nel `title` nativo e non in un Tooltip MUI: è la stessa
        // informazione con un componente in meno, ed è come lo mostra il resto del restyle.
        return (
          <span className="lc-when" title={lastRunDate ? formatAbsolute(lastRunDate) : undefined}>
            {lastRunDate ? formatRelativeDate(lastRunDate, locale) : '—'}
          </span>
        );
      },
    },
    {
      field: 'actions', headerName: t('automationsInsights.list.columns.actions'),
      width: 60, sortable: false, filterable: false,
      // Azioni riservate all'amministratore: per i non-admin la cella resta vuota.
      renderCell: (params) => (
        <span className="lc-actions">
          {isAdmin ? <RowActions row={params.row} handlers={handlers} /> : null}
        </span>
      ),
    },
  ];
}

interface RowHandlers {
  onDetail: (id: number) => void;
  onMonitor: (id: number) => void;
  onSuspend: (id: number) => void;
  onResume: (id: number) => void;
  onDelete: (id: number) => void;
}

/**
 * Menu azioni di riga (icona a tre puntini). La transizione di stato disponibile
 * dipende dallo stato corrente (ACTIVE->Sospendi, SUSPENDED->Riattiva), coerente
 * con i vincoli del backend (transizione illegale -> 400). Renderizzato solo per
 * l'amministratore (vedi colonna azioni).
 * <p>Comporre e pubblicare NON si fanno da qui: modifica, nuova versione e
 * pubblicazione vivono nel builder, che è il posto dove si vede cosa si sta
 * cambiando. L'elenco governa il ciclo di vita di un'automazione già scritta.
 */
function RowActions({ row, handlers }: { row: Automation; handlers: RowHandlers }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);
  const wrap = (fn: (id: number) => void) => () => {
    close();
    fn(row.automationDefinitionId);
  };
  const id = row.automationDefinitionId;

  return (
    <>
      <button
        type="button"
        className="lc-act"
        aria-label={t('automationsInsights.list.columns.actions')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </button>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        {/* Prima e senza condizioni: aprire un record è l'unica azione che ha senso in ogni stato,
            e dalle righe non c'è altro modo di arrivarci — non sono cliccabili. Le altre voci
            amministrano l'automazione e compaiono secondo lo stato; questa la mostra e basta. */}
        <MenuItem onClick={wrap(handlers.onDetail)}>{t('automationsInsights.list.actions.detail')}</MenuItem>
        {/* Monitoraggio ovunque tranne che sulle BOZZE, che non hanno mai prodotto un run.
            Una sospesa invece ha uno storico, ed è anzi quando è ferma che si va a vedere
            cos'aveva fatto. Stessa identica condizione del pulsante nel builder
            (AutomationForm), così le due strade verso la stessa pagina non dicono cose
            diverse sullo stesso record. */}
        {row.status !== 'draft' && (
          <MenuItem onClick={wrap(handlers.onMonitor)}>{t('automationsInsights.list.actions.monitor')}</MenuItem>
        )}
        {row.status === 'active' && (
          <MenuItem onClick={wrap(handlers.onSuspend)}>{t('automationsInsights.list.actions.suspend')}</MenuItem>
        )}
        {row.status === 'suspended' && (
          <MenuItem onClick={wrap(handlers.onResume)}>{t('automationsInsights.list.actions.resume')}</MenuItem>
        )}
        {/* Sempre visibile, anche sulle ATTIVE, che il server rifiuta di eliminare
            (ActiveNotDeletable): l'esito arriva come toast di errore invece che come
            voce assente, così l'utente sa che deve prima sospendere. */}
        <MenuItem onClick={() => { close(); handlers.onDelete(id); }} className="text-error">
          {t('automationsInsights.list.actions.delete')}
        </MenuItem>
      </Menu>
    </>
  );
}
